import React, { useState, useEffect } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

/**
 * DevBanner Component
 *
 * Displays a banner at the top of the application in development/testing environments
 * to clearly identify which worktree or environment the user is currently viewing.
 * Can be collapsed to a small tab to reduce UI interruption.
 *
 * Only renders in non-production environments.
 */
const DevBanner: React.FC = () => {
  const env = import.meta.env.VITE_ENV;
  const worktreeName = import.meta.env.VITE_WORKTREE_NAME;
  const apiUrl = import.meta.env.VITE_API_URL;

  // Persist collapsed state in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('devBannerCollapsed');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('devBannerCollapsed', String(isCollapsed));
  }, [isCollapsed]);

  // Don't show banner in production
  if (env === 'production') {
    return null;
  }

  // Determine banner color based on environment
  const getBannerColor = () => {
    switch (env) {
      case 'development':
        return 'bg-blue-600';
      case 'test':
        return 'bg-yellow-600';
      default:
        return 'bg-gray-600';
    }
  };

  // Extract port from API URL for additional context
  const getPortInfo = () => {
    if (!apiUrl) return '';
    const match = apiUrl.match(/:(\d+)/);
    return match ? `Port ${match[1]}` : '';
  };

  // Collapsed state - show small tab
  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className={`${getBannerColor()} text-white text-xs py-1.5 px-3 rounded-b-md shadow-lg fixed top-0 left-4 flex items-center gap-1.5 hover:opacity-90 transition-opacity cursor-pointer`}
        style={{ zIndex: 9999 }}
        title="Expand dev banner"
      >
        <span className="font-semibold">{env?.toUpperCase()}</span>
        {worktreeName && (
          <span className="font-mono opacity-90">| {worktreeName}</span>
        )}
        <ChevronDownIcon className="w-3.5 h-3.5" />
      </button>
    );
  }

  // Expanded state - full banner
  return (
    <div
      className={`${getBannerColor()} text-white text-sm py-2 px-4 shadow-md z-50`}
      style={{ position: 'sticky', top: 0, width: '100%' }}
    >
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="font-bold uppercase tracking-wider">
            {env} Environment
          </span>
          {worktreeName && (
            <>
              <span className="text-white/60">|</span>
              <span className="font-semibold">
                Worktree: <span className="font-mono">{worktreeName}</span>
              </span>
            </>
          )}
          {getPortInfo() && (
            <>
              <span className="text-white/60">|</span>
              <span className="text-sm opacity-90">{getPortInfo()}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs opacity-75">
            {apiUrl}
          </span>
          <button
            onClick={() => setIsCollapsed(true)}
            className="text-white/80 hover:text-white transition-colors p-1 rounded hover:bg-white/10"
            title="Collapse dev banner"
          >
            <ChevronUpIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DevBanner;
